'use client';

/**
 * SourceImport — UI field on CollectionItems. The user picks one of the landing's
 * content sources, searches it (via `/api/sources/records`), and the chosen record
 * pre-fills the document — import-once + edit locally. Single-select.
 *
 * Source-agnostic: the endpoint normalises every source through its adapter, so
 * nothing here knows which kind of system it is talking to.
 */
import { Modal } from '@/components/Modal/Modal';
import { fetchFieldDefinitions, relationshipId } from '@/utils/dynamic-fields';
import { useTenantSelection } from '@payloadcms/plugin-multi-tenant/client';
import { Button, CheckboxInput, RelationshipInput, toast, useDocumentInfo, useForm, useFormFields, useModal } from '@payloadcms/ui';
import { useCallback, useMemo, useRef, useState } from 'react';

import { mapRecordToCoreFields, mapRecordToDynamicFields } from './map-record';
import { createRequestSequence } from './request-sequence';

import './styles.scss';

import type { DynamicFieldDefinition } from '@/utils/dynamic-fields';

const SOURCE_IMPORT_MODAL_SLUG = 'source-import-modal';

interface SourceRecord {
  id: string;
  title: string;
  subtitle?: string;
  reference?: string;
}

interface SearchDescription {
  label?: string;
}

const PAGE_SIZE = 25;

interface RecordDetail {
  title?: string;
  description?: string;
  reference?: string;
  fields?: Record<string, unknown>;
}

export const SourceImport: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const dispatchFields = useFormFields(([, dispatch]) => dispatch);
  const { setModified } = useForm();
  const typeValue = useFormFields(([fields]) => fields?.type?.value as unknown);
  const currentSource = useFormFields(([fields]) => fields?.['externalSource.source']?.value as unknown);
  const { selectedTenantID } = useTenantSelection();
  const currentDynamicFields = useFormFields(([fields]) => fields?.dynamicFields?.value as Record<string, unknown> | undefined);
  const currentTitle = useFormFields(([fields]) => fields?.title?.value as unknown);
  const currentSlug = useFormFields(([fields]) => fields?.slug?.value as unknown);
  const currentDescription = useFormFields(([fields]) => fields?.description?.value as unknown);
  const { openModal, closeModal } = useModal();
  // `readOnly` is always false for a `ui` field; this is what the Save button uses.
  const { hasSavePermission } = useDocumentInfo();

  const typeId = relationshipId(typeValue);

  const [search, setSearch] = useState('');
  const [searchLabel, setSearchLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<SourceRecord[] | null>(null);
  // Carried across pages: rebuilding it per page re-hashes every record already loaded.
  const loadedIds = useRef(new Set<string>());
  const [total, setTotal] = useState<number | null>(null);
  const [sourceId, setSourceId] = useState(() => relationshipId(currentSource) ?? '');
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Newest request wins on each path, so a slow answer can never repopulate the picker (or
  // pre-fill the item) for a source the user has since moved away from. See request-sequence.ts.
  const listRequests = useMemo(() => createRequestSequence(), []);
  const importRequests = useMemo(() => createRequestSequence(), []);

  const loadRecords = useCallback(
    async (term: string, { source = sourceId, offset = 0 }: { source?: string; offset?: number } = {}) => {
      if (!source) return;
      const { signal, isStale } = listRequests.begin();
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ source, limit: String(PAGE_SIZE) });
        if (term.trim()) params.set('search', term.trim());
        if (offset) params.set('offset', String(offset));
        const res = await fetch(`/api/sources/records?${params.toString()}`, { credentials: 'include', signal });
        const body = await res.json();
        if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
        if (isStale()) return;
        const page: SourceRecord[] = Array.isArray(body.records) ? body.records : [];
        // The updater stays pure: Strict Mode runs it twice, and a second pass over a mutated
        // ref would filter every new row away.
        const append = Boolean(offset);
        if (!append) loadedIds.current = new Set();
        const fresh = page.filter((record) => !loadedIds.current.has(record.id));
        for (const record of fresh) loadedIds.current.add(record.id);
        setRecords((current) => (append && current ? [...current, ...fresh] : fresh));
        setTotal(typeof body.total === 'number' ? body.total : null);
        setSearchLabel((body.search as SearchDescription | undefined)?.label ?? null);
      } catch (e) {
        if (isStale()) return;
        setError((e as Error).message);
        if (!offset) {
          loadedIds.current.clear();
          setRecords(null);
        }
      } finally {
        if (!isStale()) setLoading(false);
      }
    },
    [sourceId, listRequests],
  );

  const importRecord = useCallback(
    async (record: SourceRecord) => {
      const { signal, isStale } = importRequests.begin();
      setImportingId(record.id);
      let detail: RecordDetail;
      let definitions: DynamicFieldDefinition[] = [];
      try {
        const params = new URLSearchParams({ source: sourceId, id: record.id });
        const [detailRes, loadedDefinitions] = await Promise.all([
          fetch(`/api/sources/records?${params.toString()}`, { credentials: 'include', signal }),
          typeId ? fetchFieldDefinitions(typeId) : [],
        ]);
        const body = await detailRes.json().catch(() => null);
        if (!detailRes.ok) throw new Error(body?.error || `Request failed (${detailRes.status})`);
        detail = body as RecordDetail;
        definitions = loadedDefinitions;
      } catch (e) {
        if (!isStale()) setError((e as Error).message);
        return;
      } finally {
        if (!isStale()) setImportingId(null);
      }

      // The source (or the record being imported) changed while this detail was in flight —
      // pre-filling now would stamp the item with the wrong source's provenance.
      if (isStale()) return;

      const set = (path: string, value: unknown) => dispatchFields({ type: 'UPDATE', path, value });
      set('externalSource.source', sourceId);
      set('externalSource.externalId', record.id);

      const sourceTitle = detail.title ?? record.title;
      const core = mapRecordToCoreFields(
        { title: sourceTitle, reference: detail.reference ?? record.reference, description: detail.description },
        { title: currentTitle, slug: currentSlug, description: currentDescription },
        { replaceExisting },
      );
      for (const [path, value] of Object.entries(core)) set(path, value);

      const mapped = mapRecordToDynamicFields(definitions, detail.fields ?? {}, currentDynamicFields ?? {}, { replaceExisting });
      const mappedCount = Object.keys(mapped).length;
      if (mappedCount) set('dynamicFields', { ...currentDynamicFields, ...mapped });

      // Dispatching values does not dirty the form, and Payload gates Save/Publish on that.
      setModified(true);

      toast.success(`Imported "${sourceTitle}" — ${mappedCount} dynamic field${mappedCount === 1 ? '' : 's'} pre-filled. Review and save`);
      closeModal(SOURCE_IMPORT_MODAL_SLUG);
    },
    [
      dispatchFields,
      setModified,
      closeModal,
      typeId,
      currentDynamicFields,
      currentTitle,
      currentSlug,
      currentDescription,
      sourceId,
      replaceExisting,
      importRequests,
    ],
  );

  if (readOnly || hasSavePermission === false || !selectedTenantID || !typeId) return null;

  return (
    <div className="field-type fv-source-import">
      <Button
        buttonStyle="secondary"
        onClick={() => {
          setError(null);
          openModal(SOURCE_IMPORT_MODAL_SLUG);
        }}
      >
        Import from a source
      </Button>

      <Modal slug={SOURCE_IMPORT_MODAL_SLUG} className="fv-source-import" aria-label="Import from a content source">
        <div className="fv-source-import__content">
          <h3 className="fv-source-import__title">Import from a content source</h3>

          <RelationshipInput
            path="source-import-source"
            label="Content source"
            relationTo={['content-source']}
            value={sourceId ? { relationTo: 'content-source', value: sourceId } : null}
            hasMany={false}
            allowCreate={false}
            allowEdit={false}
            required
            filterOptions={{ 'content-source': { tenant: { equals: selectedTenantID } } }}
            onChange={(value) => {
              const next = ((value as { value?: unknown } | null)?.value as string | null) ?? '';
              // Anything still in flight belongs to the previous source: discard it, or it will
              // refill this list (and its `loading`) with records the picker no longer represents.
              listRequests.cancel();
              importRequests.cancel();
              setLoading(false);
              setImportingId(null);
              setSourceId(next);
              loadedIds.current.clear();
              setRecords(null);
              setTotal(null);
              setError(null);
              setSearchLabel(null);
            }}
          />

          {sourceId && (
            <div className="fv-source-import__search">
              {/* Wrapped in Payload's own field classes so the input inherits its `formInput` styling. */}
              <div className="field-type text">
                <input
                  type="text"
                  aria-label={searchLabel ?? 'Search this source'}
                  placeholder={searchLabel ? `${searchLabel}…` : 'Search this source…'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void loadRecords(search);
                  }}
                />
              </div>
              <Button buttonStyle="primary" onClick={() => void loadRecords(search)} disabled={loading}>
                {loading ? 'Searching…' : 'Search'}
              </Button>
            </div>
          )}

          {sourceId && (
            <div className="field-type checkbox fv-source-import__replace">
              <CheckboxInput
                id="source-import-replace-existing"
                name="source-import-replace-existing"
                label="Replace values that already have content"
                checked={replaceExisting}
                onToggle={(event) => setReplaceExisting(event.target.checked)}
              />
              <p className="fv-source-import__meta">
                Off: the import only fills in fields that are empty. On: replaces text that is already there. It never deletes anything, if
                the source has no value for a field, your text stays. It never changes the slug.
              </p>
            </div>
          )}

          {error && <p className="field-error">Error: {error}</p>}
          {!error && records?.length === 0 && <p className="fv-source-import__meta">No records found.</p>}

          {!error && records && records.length > 0 && (
            <p className="fv-source-import__meta">
              {total != null && total > records.length ? `Showing ${records.length} of ${total} matches` : `${records.length} matches`}
            </p>
          )}

          <ul className="fv-source-import__results">
            {records?.map((record) => (
              <li key={record.id} className="fv-source-import__result">
                <span>
                  <strong>{record.title}</strong>{' '}
                  <span className="fv-source-import__meta">
                    #{record.id}
                    {record.subtitle ? ` · ${record.subtitle}` : ''}
                  </span>
                </span>
                <Button buttonStyle="secondary" onClick={() => void importRecord(record)} disabled={importingId !== null}>
                  {importingId === record.id ? 'Importing…' : 'Select'}
                </Button>
              </li>
            ))}
          </ul>

          {records && total != null && records.length < total && (
            <Button buttonStyle="secondary" onClick={() => void loadRecords(search, { offset: records.length })} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
};
